import React, { useMemo } from "react";
import { Box } from "@mui/material";
import DOMPurify from "dompurify";

// Force links to open safely (rich-text bodies may contain user links).
if (typeof DOMPurify.addHook === "function" && !DOMPurify.__rtvHook) {
  DOMPurify.__rtvHook = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer nofollow");
    }
  });
}

const looksLikeHtml = (s) => /<\/?(p|div|br|span|strong|em|b|i|u|s|ul|ol|li|a|img|h[1-6]|blockquote|pre|code|table)\b/i.test(String(s || ""));

/**
 * Renders a stored rich-text body. New content is sanitized HTML; legacy
 * records hold plain text, which we render with preserved line breaks. Safe to
 * drop in anywhere a plain-text field used to be shown.
 */
export default function RichTextView({ html, sx }) {
  const value = String(html ?? "");
  const isHtml = looksLikeHtml(value);
  const clean = useMemo(
    () => (isHtml ? DOMPurify.sanitize(value, { USE_PROFILES: { html: true } }) : ""),
    [value, isHtml]
  );

  const proseSx = {
    fontSize: "0.9rem",
    lineHeight: 1.6,
    color: "text.primary",
    wordBreak: "break-word",
    "& p": { m: 0, mb: 1, "&:last-child": { mb: 0 } },
    "& ul, & ol": { m: 0, mb: 1, pl: 3 },
    "& li": { mb: 0.3 },
    "& blockquote": { m: 0, my: 1, pl: 1.5, borderLeft: "3px solid #c9bdf0", color: "text.secondary" },
    "& h1, & h2, & h3, & h4": { mt: 1.5, mb: 0.75, fontWeight: 700, lineHeight: 1.3 },
    "& h1": { fontSize: "1.3rem" }, "& h2": { fontSize: "1.15rem" }, "& h3": { fontSize: "1.02rem" },
    "& a": { color: "#6C50BF", fontWeight: 600 },
    "& code": { fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.82em", bgcolor: "#eef0f6", px: 0.6, py: 0.1, borderRadius: "4px" },
    "& pre": { bgcolor: "#f6f8fa", p: 1.25, borderRadius: "8px", overflowX: "auto", my: 1 },
    "& pre code": { bgcolor: "transparent", p: 0 },
    "& img": { maxWidth: "100%", borderRadius: "6px" },
    "& table": { borderCollapse: "collapse", my: 1, "& th, & td": { border: "1px solid #dfe2ec", px: 1, py: 0.4 } },
    ...sx,
  };

  if (!value.trim()) return null;
  if (isHtml) {
    return <Box sx={proseSx} dangerouslySetInnerHTML={{ __html: clean }} />;
  }
  // Legacy plain text — preserve newlines.
  return <Box sx={{ ...proseSx, whiteSpace: "pre-wrap" }}>{value}</Box>;
}
